import type { NavigationProps } from './types'
import type { MenuItem } from '@/cms/fields/types/common'
import * as NavigationMenu from '@radix-ui/react-navigation-menu'
import * as Portal from '@radix-ui/react-portal'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import cn from 'classnames'
import * as React from 'react'
import { RemoveScrollBar } from 'react-remove-scroll-bar'
import { createHeaderClickEvent } from '@/domain/header/analytics'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import styles from './DesktopNavigation.module.css'
import { HeaderLink } from './HeaderLink'
import { Menu, MenuTrigger, NestedSubMenu, SubMenu } from './Menu'
import { Navbar } from './Navbar'

export function DesktopNavbarItem({ className, ...props }: NavigationMenu.NavigationMenuItemProps) {
  return <Navbar.Item className={cn(styles.desktopNavbarItem, className)} {...props} />
}

export function DesktopNavigation({ data, extraData, headerWrapperElement }: NavigationProps) {
  const { sendEvent } = useAnalytics()
  const [openSubmenuId, setOpenSubmenuId] = React.useState('')

  const handleMenuItemClick = (result: string[], children: MenuItem[] | undefined) => {
    sendEvent(
      createHeaderClickEvent({
        result,
        hasChildren: children && children.length > 0,
      }),
    )
  }

  return (
    <>
      <Navbar className={styles.navbar} value={openSubmenuId} onValueChange={setOpenSubmenuId}>
        <Navbar.List>
          {data && (
            <DesktopNavbarItem>
              <MenuTrigger
                onClick={() => handleMenuItemClick([data.text], data.children)}
                onPointerMove={(e: React.PointerEvent) => e.preventDefault()}
                onPointerLeave={(e: React.PointerEvent) => e.preventDefault()}
              >
                {data.text}
                <Icon glyph="ChevronDownExtraSmall" />
              </MenuTrigger>
              <Menu>
                {data.children?.map((menuItem, menuItemKey) => {
                  const menuItemChildren = menuItem.children

                  return (
                    <Menu.Item
                      onClick={
                        menuItemChildren && menuItemChildren.length > 0
                          ? () => handleMenuItemClick([data.text, menuItem.text], menuItemChildren)
                          : undefined
                      }
                      key={menuItemKey}
                    >
                      {menuItemChildren && menuItemChildren.length > 0
                        ? (
                            <SubMenu label={menuItem.text}>
                              {menuItemChildren.map((subMenuItem, subMenuItemKey) => {
                                const subMenuItemChildren = subMenuItem.children

                                return (
                                  <SubMenu.Item
                                    onClick={
                                      subMenuItemChildren && subMenuItemChildren.length > 0
                                        ? () => handleMenuItemClick([data.text, menuItem.text, subMenuItem.text], subMenuItemChildren)
                                        : undefined
                                    }
                                    key={subMenuItemKey}
                                  >
                                    {subMenuItemChildren && subMenuItemChildren.length > 0
                                      ? (
                                          <NestedSubMenu label={subMenuItem.text}>
                                            {subMenuItemChildren.map((nestedSubMenuItem, nestedSubMenuItemKey) => (
                                              <HeaderLink
                                                onClick={() =>
                                                  handleMenuItemClick(
                                                    [data.text, menuItem.text, subMenuItem.text, nestedSubMenuItem.text],
                                                    nestedSubMenuItem.children,
                                                  )}
                                                key={nestedSubMenuItemKey}
                                                href={nestedSubMenuItem.href}
                                              >
                                                {nestedSubMenuItem.text}
                                              </HeaderLink>
                                            ))}
                                          </NestedSubMenu>
                                        )
                                      : (
                                          <HeaderLink
                                            onClick={() =>
                                              handleMenuItemClick([data.text, menuItem.text, subMenuItem.text], subMenuItemChildren)}
                                            href={subMenuItem.href}
                                          >
                                            {subMenuItem.text}
                                          </HeaderLink>
                                        )}
                                  </SubMenu.Item>
                                )
                              })}
                            </SubMenu>
                          )
                        : (
                            <HeaderLink
                              onClick={() => handleMenuItemClick([data.text, menuItem.text], menuItemChildren)}
                              href={menuItem.href}
                            >
                              {menuItem.text}
                            </HeaderLink>
                          )}
                    </Menu.Item>
                  )
                })}
              </Menu>
            </DesktopNavbarItem>
          )}
          {extraData?.map((menuItem, menuItemKey) => (
            <DesktopNavbarItem key={menuItemKey}>
              <HeaderLink onClick={() => handleMenuItemClick([menuItem.text], undefined)} href={menuItem.href}>
                {menuItem.text}
              </HeaderLink>
            </DesktopNavbarItem>
          ))}
        </Navbar.List>

        <div className={styles.activeContent}>
          <div className={styles.activeContentInner}>
            <NavigationMenu.Viewport className={styles.viewport} forceMount />
          </div>
        </div>
      </Navbar>
      {openSubmenuId && <RemoveScrollBar />}
      <Portal.Root container={headerWrapperElement}>
        <div className={cn(styles.overlay, Boolean(openSubmenuId) && styles.visible)} />
      </Portal.Root>
    </>
  )
}
